import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  FormEvent,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { cardapioService } from "../../../services/cardapioService";
import { CardapioFormData, CardapioItem } from "../../../types/cardapio";

type CardapioFormState = {
  id: number | undefined;
  nome: string;
  ingredientes: string[];
  tipo: "doce" | "salgada";
  foto: File | null;
  fotoUrl: string | null;
};

interface FilterState {
  nome: string;
  ingrediente: string;
}

interface CardapioContextType {
  sabores: CardapioItem[];
  isLoading: boolean;
  isModalOpen: boolean;
  formState: CardapioFormState;
  isSaving: boolean;
  tableData: Array<ReactNode[]>;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  filters: FilterState;
  filterErrors: Record<string, string>;
  handleFieldChange: (name: string, value: string) => void;
  handleSearch: () => boolean;
  handleClear: () => void;
  clearFilterErrors: () => void;
  setCurrentPage: (page: number) => void;
  openCreateModal: () => void;
  openEditModal: (item: CardapioItem) => void;
  closeModal: () => void;
  handleDelete: (item: CardapioItem) => void;
  handleSubmit: (event: FormEvent<HTMLFormElement>) => void;
  setFormNome: (nome: string) => void;
  setFormIngredientes: (ingredientes: string[]) => void;
  setFormTipo: (tipo: "doce" | "salgada") => void;
  setFormFoto: (foto: File | null) => void;
  removerFotoAtual: () => void;
}

const CardapioContext = createContext<CardapioContextType | undefined>(
  undefined
);

const getDefaultFormState = (): CardapioFormState => ({
  id: undefined,
  nome: "",
  ingredientes: [],
  tipo: "salgada",
  foto: null,
  fotoUrl: null,
});
const EMPTY_SABORES: CardapioItem[] = [];

export const CardapioProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formState, setFormState] = useState<CardapioFormState>(
    getDefaultFormState()
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState<FilterState>({
    nome: "",
    ingrediente: "",
  });
  const [searchFilters, setSearchFilters] = useState<FilterState>({
    nome: "",
    ingrediente: "",
  });
  const [filterErrors, setFilterErrors] = useState<Record<string, string>>({});
  const itemsPerPage = 10;

  const searchNome = searchFilters.nome?.trim() || undefined;
  const searchIngrediente = searchFilters.ingrediente?.trim() || undefined;

  const { data: paginatedData, isLoading } = useQuery({
    queryKey: [
      "cardapio",
      currentPage,
      itemsPerPage,
      searchNome,
      searchIngrediente,
    ],
    queryFn: () =>
      cardapioService.getSaboresPaginated(
        currentPage,
        itemsPerPage,
        searchNome,
        searchIngrediente
      ),
  });
  const isLegacyArrayResponse = Array.isArray(paginatedData);
  const legacyData = isLegacyArrayResponse ? paginatedData : EMPTY_SABORES;
  const sabores = useMemo(
    () =>
      isLegacyArrayResponse
        ? legacyData
        : (paginatedData?.data ?? EMPTY_SABORES),
    [isLegacyArrayResponse, legacyData, paginatedData]
  );
  const totalItems = isLegacyArrayResponse
    ? legacyData.length
    : paginatedData?.total || 0;
  const totalPages = isLegacyArrayResponse
    ? Math.ceil(legacyData.length / itemsPerPage)
    : paginatedData?.totalPages || 0;
  const apiBaseUrl = process.env.REACT_APP_API_URL || "http://localhost:3001";

  const invalidateCardapio = () => {
    queryClient.invalidateQueries({ queryKey: ["cardapio"] });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormState(getDefaultFormState());
  };

  const createMutation = useMutation({
    mutationFn: (payload: CardapioFormData) =>
      cardapioService.createSabor(payload),
    onSuccess: () => {
      toast.success("Sabor cadastrado com sucesso.");
      invalidateCardapio();
      closeModal();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { id: number; data: CardapioFormData }) =>
      cardapioService.updateSabor(payload.id, payload.data),
    onSuccess: () => {
      toast.success("Sabor atualizado com sucesso.");
      invalidateCardapio();
      closeModal();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => cardapioService.deleteSabor(id),
    onSuccess: () => {
      toast.success("Sabor removido com sucesso.");
      invalidateCardapio();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const openCreateModal = () => {
    setFormState(getDefaultFormState());
    setIsModalOpen(true);
  };

  const openEditModal = (item: CardapioItem) => {
    setFormState({
      id: item.id,
      nome: item.nome,
      ingredientes: [...item.ingredientes],
      tipo: item.tipo,
      foto: null,
      fotoUrl: item.fotoUrl,
    });
    setIsModalOpen(true);
  };

  const handleDelete = (item: CardapioItem) => {
    const confirmed = window.confirm(`Deseja remover o sabor "${item.nome}"?`);
    if (!confirmed) return;
    deleteMutation.mutate(item.id);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nome = formState.nome.trim();
    const ingredientes = formState.ingredientes
      .map((item) => item.trim())
      .filter(Boolean);

    if (!nome) {
      toast.error("Informe o nome do sabor.");
      return;
    }

    if (!ingredientes.length) {
      toast.error("Adicione ao menos um ingrediente.");
      return;
    }

    const payloadWithExtras: CardapioFormData = {
      nome,
      ingredientes,
      tipo: formState.tipo,
      foto: formState.foto,
    };

    if (formState.id) {
      updateMutation.mutate({ id: formState.id, data: payloadWithExtras });
      return;
    }

    createMutation.mutate(payloadWithExtras);
  };

  const setFormNome = (nome: string) => {
    setFormState((prev) => ({ ...prev, nome }));
  };

  const setFormIngredientes = (ingredientes: string[]) => {
    setFormState((prev) => ({ ...prev, ingredientes }));
  };

  const setFormTipo = (tipo: "doce" | "salgada") => {
    setFormState((prev) => ({ ...prev, tipo }));
  };

  const setFormFoto = (foto: File | null) => {
    setFormState((prev) => ({ ...prev, foto }));
  };

  const removerFotoAtual = async () => {
    if (!formState.id) {
      setFormState((prev) => ({ ...prev, fotoUrl: null, foto: null }));
      return;
    }
    try {
      await cardapioService.deleteSaborFoto(formState.id);
      toast.success("Foto removida com sucesso.");
      setFormState((prev) => ({ ...prev, fotoUrl: null, foto: null }));
      invalidateCardapio();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Erro ao remover foto.";
      toast.error(message);
    }
  };

  const handleFieldChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (filterErrors[name]) {
      setFilterErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSearch = (): boolean => {
    setFilterErrors({});
    setSearchFilters(filters);
    setCurrentPage(1);
    return true;
  };

  const clearFilterErrors = () => setFilterErrors({});

  const handleClear = () => {
    setFilters({ nome: "", ingrediente: "" });
    setSearchFilters({ nome: "", ingrediente: "" });
    setCurrentPage(1);
  };

  const tableData = useMemo(
    () =>
      sabores.map((item) => {
        const fotoSrc = item.fotoUrl ? `${apiBaseUrl}${item.fotoUrl}` : null;
        return [
          fotoSrc ? (
            <img
              src={fotoSrc}
              alt={`Foto do sabor ${item.nome}`}
              className="h-12 w-12 rounded-md object-cover"
            />
          ) : (
            <span className="text-xs text-neutral-400">Sem foto</span>
          ),
          item.nome,
          item.tipo === "doce" ? "Doce" : "Salgada",
          item.ingredientes.join(", "),
        ];
      }),
    [sabores, apiBaseUrl]
  );

  return (
    <CardapioContext.Provider
      value={{
        sabores,
        isLoading,
        isModalOpen,
        formState,
        isSaving: createMutation.isPending || updateMutation.isPending,
        tableData,
        currentPage,
        totalPages,
        totalItems,
        itemsPerPage,
        filters,
        filterErrors,
        handleFieldChange,
        handleSearch,
        handleClear,
        clearFilterErrors,
        setCurrentPage,
        openCreateModal,
        openEditModal,
        closeModal,
        handleDelete,
        handleSubmit,
        setFormNome,
        setFormIngredientes,
        setFormTipo,
        setFormFoto,
        removerFotoAtual,
      }}
    >
      {children}
    </CardapioContext.Provider>
  );
};

export const useCardapio = () => {
  const context = useContext(CardapioContext);
  if (!context) {
    throw new Error("useCardapio must be used within CardapioProvider");
  }
  return context;
};
